import {createAction} from '@reduxjs/toolkit'
import {ExperimentNames, hasSeatingPlan, isDraft} from '@wix/wix-events-commons-statics'
import {callAPI, createActions} from '../../../../commons/actions/data-action-helper'
import {isTemplate} from '../../../../commons/selectors/instance'
import {UserRole} from '../constants/constants'
import {isUpgradeNeededForCheckout} from '../selectors/checkout-options'
import {getEvent} from '../selectors/event'
import {isEventPreview} from '../selectors/navigation'
import {isRequestPending, isRequestPendingOld} from '../selectors/pending-requests'
import {getSeatsReservationQuantities} from '../selectors/seating/places'
import {getReservationQuantities} from '../selectors/selected-tickets'
import {getReservationData} from '../selectors/tickets'
import {Interaction} from '../services/logger'
import {createAsyncAction} from '../services/redux-toolkit'
import {GetState} from '../types/state'
import {StoreExtraArgs} from '../types/store-extra-arg'
import {openCheckoutUnavailable, openUpgradeToPremium, openUpgradeToPremiumOld} from './modals'
import {
  NAVIGATE_OLD,
  navigate,
  navigateTo,
  navigateToMobileCheckout,
  navigateToMobileCheckoutOld,
  navigateToTicketForm,
  navigateToTicketFormOld,
} from './navigation'

export const RESERVE_TICKETS_OLD = createActions('RESERVE_TICKETS_OLD')

export const reserveTickets =
  (eventId: string) =>
  async (dispatch: Function, getState: GetState, {wixCodeApi, flowAPI}: StoreExtraArgs) => {
    const state = getState()
    const upgradeNeeded = isUpgradeNeededForCheckout(state)
    const owner = wixCodeApi.user.currentUser.role === UserRole.ADMIN
    const event = getEvent(state)
    const mobile = flowAPI.environment.isMobile
    const useReduxToolkitEnabled = flowAPI.experiments.enabled(ExperimentNames.UseDetailsPageReduxToolkit)

    const relocatedUpgradeModalEnabled = flowAPI.experiments.enabled(ExperimentNames.RelocatedUpgradeModal)

    if (isTemplate(state) || isEventPreview(state) || isDraft(event)) {
      return mobile
        ? useReduxToolkitEnabled
          ? dispatch(navigateToMobileCheckout())
          : dispatch(navigateToMobileCheckoutOld())
        : useReduxToolkitEnabled
        ? dispatch(navigateToTicketForm())
        : dispatch(navigateToTicketFormOld())
    }

    if (upgradeNeeded && (mobile || !owner)) {
      return dispatch(openCheckoutUnavailable())
    }

    const ticketQuantities: wix.events.ticketing.TicketReservationQuantity[] = hasSeatingPlan(event)
      ? getSeatsReservationQuantities(state)
      : getReservationQuantities(state)

    if (upgradeNeeded && owner) {
      const {proceed} = await dispatch(
        relocatedUpgradeModalEnabled ? openUpgradeToPremium() : openUpgradeToPremiumOld(),
      ).unwrap()
      if (!proceed) {
        return
      }
    }

    return useReduxToolkitEnabled
      ? dispatch(makeReservationAndNavigate({eventId, ticketQuantities}))
      : dispatch(makeReservationOld({eventId, ticketQuantities}))
  }

interface CancelReservationParams {
  eventId: string
  reservationId: string
}

export const cancelReservation = createAsyncAction<{}, CancelReservationParams>(
  'CANCEL_RESERVATION',
  ({eventId, reservationId}, {extra: {serverApi}}) => {
    if (eventId && reservationId) {
      return serverApi.cancelReservation(eventId, reservationId)
    }
  },
)

export const makeReservation = createAsyncAction<
  {id: string; expires: string; reservations?: any[]},
  {eventId: string; ticketQuantities: wix.events.ticketing.TicketReservationQuantity[]}
>('MAKE_RESERVATION', async ({eventId, ticketQuantities}, {getState, extra: {serverApi}}) => {
  const state = getState()

  if (!state.demoTickets?.length) {
    return serverApi.makeReservation(eventId, ticketQuantities)
  } else {
    return {
      id: 'demo',
      reservations: [],
      expires: new Date(new Date().getTime() + 20 * 60 * 1000).toISOString(),
    }
  }
})

export const makeReservationAndNavigate = createAsyncAction<
  void,
  {eventId: string; ticketQuantities: wix.events.ticketing.TicketReservationQuantity[]}
>(
  'MAKE_RESERVATION_AND_NAVIGATE',
  async ({eventId, ticketQuantities}, {dispatch, extra: {flowAPI, logger}}) => {
    logger.startInteraction(Interaction.CreateReservation)

    const reservation = await dispatch(makeReservation({eventId, ticketQuantities})).unwrap()
    const reservationId = flowAPI.experiments.enabled(ExperimentNames.NavigateToCheckoutWithReservationId)
      ? reservation?.id
      : undefined

    flowAPI.environment.isMobile
      ? dispatch(navigateToMobileCheckout(reservationId))
      : dispatch(navigateToTicketForm(reservationId))
  },
  {
    condition: (_, {getState}) => {
      const state = getState()

      return !isRequestPending(state, navigate.typePrefix) && !isRequestPending(state, makeReservation.typePrefix)
    },
  },
)

export const makeReservationOld = createAsyncAction<
  void,
  {eventId: string; ticketQuantities: wix.events.ticketing.TicketReservationQuantity[]}
>('MAKE_RESERVATION_OLD', async ({eventId, ticketQuantities}, {getState, dispatch, extra: {flowAPI, logger}}) => {
  const state = getState()

  if (!isRequestPendingOld(state, NAVIGATE_OLD.REQUEST) && !isRequestPendingOld(state, RESERVE_TICKETS_OLD.REQUEST)) {
    let reservationId: string | undefined
    logger.startInteraction(Interaction.CreateReservation)

    if (!state.demoTickets?.length) {
      const reservation = await dispatch(callAPI(RESERVE_TICKETS_OLD, eventId, ticketQuantities) as any)

      reservationId = flowAPI.experiments.enabled(ExperimentNames.NavigateToCheckoutWithReservationId)
        ? reservation?.id
        : undefined
    } else {
      dispatch({
        type: RESERVE_TICKETS_OLD.SUCCESS,
        payload: {
          id: 'demo',
          reservations: [],
          expires: new Date(new Date().getTime() + 20 * 60 * 1000).toISOString(),
        },
      })
    }

    flowAPI.environment.isMobile
      ? dispatch(navigateToMobileCheckoutOld(reservationId))
      : dispatch(navigateToTicketFormOld(reservationId))
  }
})

export const clearReservation = createAction('CLEAR_RESERVATION')

export const clearReservationError = createAction<boolean>('CLEAR_RESERVATION_ERROR')

// For Ecom checkout development
export const initiateEcomCheckoutFlow = createAsyncAction(
  'INITIATE_ECOM_CHECKOUT_FLOW',
  async (_, {getState, dispatch, extra: {serverApi}}) => {
    const state = getState()
    const ticketsForReservation = getReservationData(state)

    const {
      ticketReservation: {id: ticketReservationId},
    } = await serverApi.createTicketReservation(ticketsForReservation)

    const {checkoutId} = await serverApi.createCheckout(ticketReservationId)

    dispatch(navigateTo(`/checkout?checkoutId=${checkoutId}`))
  },
)

export const addTicketsToCart = createAsyncAction(
  'ADD_TICKETS_TO_CART',
  async (_, {getState, extra: {serverApi, wixCodeApi}}) => {
    const state = getState()
    const ticketsForReservation = getReservationData(state)

    const response = await serverApi.addTicketsToCurrentTicketReservation(ticketsForReservation)

    // @ts-expect-error
    wixCodeApi.ecom.openSideCart()

    return response
  },
)
